import React, { useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import { useBaseEntityInfo } from "../../../../Hooks/usePagesContent";
import { useSOHBuckets } from "../utils/sohBuckets";
import SOHScatterChart from "../../../../Charts/Scatter/SOHScatterChart";
import { countUnavailableBatteries } from "../../../../Charts/Scatter/scatterBucketUtils";

// Maps org meta axis x values to battery data field keys + labels
const AXIS_X_TO_FILTER = {
  addedOn:    { key: "age",       label: "Age"        },
  cycleCount: { key: "bmsCycles", label: "BMS Cycles" },
  odometer:   { key: "odometer",  label: "Odometer"   },
  latestSOHPredictionOdometer: { key: "latestSOHPredictionOdometer", label: "Odometer" },
};

const DEFAULT_X_FILTERS = [
  { key: "age",       label: "Age"        },
  { key: "bmsCycles", label: "BMS Cycles" },
];

const DETAILS_SCATTER_ID = "soh-details-scatter-chart";

/**
 * SOH details scatter chart.
 * Thin wrapper around SOHScatterChart — groups pre-classified batteries by
 * category and delegates all rendering to the shared component.
 *
 * Props
 *   batteries          — filtered array of battery objects (have .category set)
 *   selectedCategories — Set<string> from parent (legend filter)
 *   onCategorySelect   — callback(name) to toggle a category
 *   activeXFilter      — "age" | "bmsCycles" | "odometer" | null
 *   onXFilterChange    — callback(key)
 *   chartConfig        — from org meta SOHDetails.charts.sohScatterChart
 */
const SOHDetailsChart = ({
  batteries = [],
  selectedCategories,
  onCategorySelect,
  activeXFilter,
  onXFilterChange,
  chartConfig = {},
}) => {
  const { baseEntityType } = useBaseEntityInfo();
  const isVehicle  = baseEntityType === "vehicle";
  const sohBuckets = useSOHBuckets();

  // Derive x-axis filter pills from org meta axisCombinations
  const xFilters = useMemo(() => {
    const combinations = chartConfig.axisCombinations || [];
    if (combinations.length === 0) return DEFAULT_X_FILTERS;
    return combinations
      .map((combo) => {
        const base = AXIS_X_TO_FILTER[combo.x];
        if (!base) return null;
        // org-meta title (if set) overrides both the filter pill and the axis title
        return combo.title ? { ...base, label: combo.title, axisLabel: combo.title } : base;
      })
      .filter(Boolean)
      .filter((f, i, arr) => arr.findIndex((a) => a.key === f.key) === i);
  }, [chartConfig.axisCombinations]);

  // Set default active x filter from config when xFilters are resolved
  const defaultXFilter = useMemo(() => {
    const defaultCombo = (chartConfig.axisCombinations || []).find((c) => c.isDefault);
    if (defaultCombo) return AXIS_X_TO_FILTER[defaultCombo.x]?.key ?? null;
    return xFilters[0]?.key ?? null;
  }, [chartConfig.axisCombinations, xFilters]);

  useEffect(() => {
    if (activeXFilter === null && defaultXFilter !== null) {
      onXFilterChange(defaultXFilter);
    }
  }, [defaultXFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const batteriesByVisibleCategory = useMemo(
    () =>
      sohBuckets
        .filter((cat) => selectedCategories.size === 0 || selectedCategories.has(cat.name))
        .map((cat) => ({
          category:  cat,
          batteries: batteries.filter((b) => b.category === cat.name),
        })),
    [batteries, selectedCategories, sohBuckets]
  );

  // Legend count for "SOH Unavailable", independent of the current selection.
  const unavailableCount = useMemo(
    () => countUnavailableBatteries(batteries, sohBuckets, "category"),
    [batteries, sohBuckets]
  );

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <Box sx={{ overflowX: "auto", overflowY: "hidden", width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <Box sx={{ width: "max(100%, 700px)", minWidth: 0 }}>
          <SOHScatterChart
            batteriesByVisibleCategory={batteriesByVisibleCategory}
            selectedCategories={selectedCategories}
            onCategorySelect={onCategorySelect}
            activeXFilter={activeXFilter}
            onXFilterChange={onXFilterChange}
            xFilters={xFilters}
            chartId={DETAILS_SCATTER_ID}
            height={360}
            showLegend={true}
            isVehicle={isVehicle}
            tooltipFields={chartConfig?.tooltipFields ?? null}
            tooltipIdentifier={chartConfig?.tooltipIdentifier ?? null}
            unavailableCount={unavailableCount}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SOHDetailsChart;
